/**
 * TypeScript types for Tutela Ignis API
 */

// Risk levels
export type RiskLevel = 'Low' | 'Moderate' | 'High' | 'Extreme';

// Contributing factor to fire risk
export interface ContributingFactor {
  feature: string;
  value: number;
  weight: number;
  description: string;
}

// Point risk prediction response
export interface RiskPrediction {
  lat: number;
  lon: number;
  distrito: string | null;
  concelho: string | null;
  prediction_date: string;
  for_date: string;
  fire_probability: number;
  risk_level: RiskLevel;
  confidence_lower: number | null;
  confidence_upper: number | null;
  contributing_factors: ContributingFactor[];
  model_version: string;
  created_at: string;
}

// Area risk response
export interface AreaRiskResponse {
  for_date: string;
  bbox: {
    west: number;
    south: number;
    east: number;
    north: number;
  };
  total_cells: number;
  risk_summary: Record<RiskLevel, number>;
  high_risk_cells: RiskPrediction[];
  model_version: string;
}

// Fire event
export interface Fire {
  fire_id: string;
  source: 'icnf' | 'firms' | 'merged';
  lat: number;
  lon: number;
  start_datetime: string;
  end_datetime: string | null;
  burned_area_ha: number | null;
  distrito: string | null;
  concelho: string | null;
  tipo_causa: string | null;
  confidence_score: number | null;
  distance_km?: number;
}

// Alert
export interface Alert {
  alert_id: string;
  email: string;
  lat: number;
  lon: number;
  radius_km: number;
  risk_threshold: RiskLevel;
  is_active: boolean;
  last_triggered: string | null;
  created_at: string;
}

// Alert creation request
export interface AlertCreateRequest {
  email: string;
  lat: number;
  lon: number;
  radius_km?: number;
  risk_threshold?: RiskLevel;
}

// Health check response
export interface HealthResponse {
  status: 'ok' | 'degraded';
  latest_prediction_date: string | null;
  model_version: string | null;
  fires_count: number | null;
  grid_cells: number | null;
  uptime_seconds: number | null;
}

// Realtime risk prediction response
export interface RealtimeRiskResponse {
  lat: number;
  lon: number;
  fire_probability: number;
  risk_level: RiskLevel;
  confidence_lower: number | null;
  confidence_upper: number | null;
  seasonal_percentile: number | null;
  seasonal_anomaly: boolean | null;
  weather_source: string;
  station_id: string | null;
  station_distance_km: number | null;
  ffmc: number | null;
  data_age_seconds: number | null;
  model_version: string;
  timestamp: string;
}

// Simulation request
export interface SimulationRequest {
  ignition_lat: number;
  ignition_lon: number;
  wind_speed_ms?: number | null;
  wind_direction_deg?: number | null;
  temperature_c?: number | null;
  humidity_pct?: number | null;
  fwi?: number | null;
  isi?: number | null;
  ffmc?: number | null;
  dc?: number | null;
  bui?: number | null;
  max_hours?: number;
  radius_km?: number;
  resolution_m?: number;
  enable_spotting?: boolean;
  enable_suppression?: boolean;
  use_realtime_weather?: boolean;
  use_diurnal_cycle?: boolean;
  weather_timeline?: Record<string, number>[] | null;
  perimeter_geojson?: Record<string, unknown> | null;
  firms_auto_detect?: boolean;
  seed?: number | null;
}

// Simulation response
export interface SimulationResponse {
  burned_area_ha: number;
  burned_cells_count: number;
  spot_fires_count: number;
  timesteps_run: number;
  elapsed_ms: number;
  resolution_m: number;
  weather_source: string;
  suppression_factor: number;
  burned_cells: BurnedCell[];
  burned_geojson: GeoJSON.FeatureCollection;
}

// Burned cell from simulation
export interface BurnedCell {
  lat: number;
  lon: number;
  timestep_burned: number;
}

// Country-wide risk prediction response
export interface CountryRiskResponse {
  run_id: string;
  prediction_time: string;
  for_date: string;
  model_version: string;
  total_cells: number;
  risk_summary: Record<string, number>;
  anomaly_count?: number;
  baseline_month?: number;
  cells: number[][]; // [[lat, lon, prob, risk_code, percentile?], ...]
}

// Country prediction run info
export interface CountryRunInfo {
  model_run_id: string;
  prediction_time: string | null;
  for_date: string | null;
  total_cells: number;
  risk_summary: Record<string, number>;
  is_active: boolean;
}

// Risk code mapping
export const RISK_CODE_TO_LEVEL: Record<number, RiskLevel> = {
  0: 'Low',
  1: 'Moderate',
  2: 'High',
  3: 'Extreme',
};

// Grid cell for map display
export interface GridCell {
  cell_id: number;
  lat: number;
  lon: number;
  fire_probability: number;
  risk_level: RiskLevel;
}

// Portugal bounds
export const PORTUGAL_BOUNDS = {
  north: 42.15,
  south: 36.96,
  west: -9.5,
  east: -6.19,
  center: { lat: 39.5, lon: -8.0 },
} as const;

// Risk level colors
export const RISK_COLORS: Record<RiskLevel, string> = {
  // Baixo is deliberately NOT green. On a typical day ~97% of a concelho is Low, so a
  // green ramp paints almost the whole map with the universal "all clear" colour and the
  // product reads as "nothing to worry about" — the opposite of the signal, and it drowns
  // the few cells that carry the risk. Neutral slate recedes so Moderado+ is what the eye
  // finds. (2026-07-15, from looking at the VFX console on a calm day.)
  Low: '#64748b',      // slate — neutral background, not an all-clear
  Moderate: '#eab308', // yellow
  High: '#f59e0b',     // amber/orange (close to moderate)
  Extreme: '#dc2626',  // dark red
};

// Risk level thresholds (upper bound of each band). Mirrors the backend
// DEFAULT_THRESHOLDS (moderate 0.05 / high 0.10 / extreme 0.20), rescaled
// 2026-06-04. Not used to classify client-side (risk_level comes from the API),
// kept in sync to avoid drift.
export const RISK_THRESHOLDS = {
  Low: 0.05,
  Moderate: 0.10,
  High: 0.20,
  Extreme: 1.0,
} as const;

// Burned area colors for fire history
export const BURNED_AREA_COLORS = {
  small: '#22c55e',    // < 1 ha - green
  medium: '#eab308',   // 1-10 ha - yellow
  large: '#E85D3D',    // 10-100 ha - fire warning
  severe: '#dc2626',   // > 100 ha - dark red
} as const;

// Training run summary
export interface RunSummary {
  run_id: string;
  timestamp: string | null;
  is_active: boolean;
  feature_count: number;
  metrics: Record<string, Record<string, number>>;
  hyperparams: Record<string, unknown>;
}

// Side-by-side run comparison
export interface RunComparison {
  run_a: RunSummary;
  run_b: RunSummary;
  metric_deltas: Record<string, number>;
  top_features_a: FeatureImportance[];
  top_features_b: FeatureImportance[];
}

// Feature importance entry
export interface FeatureImportance {
  feature: string;
  importance: number;
}

// Get burned area color based on hectares
export function getBurnedAreaColor(hectares: number | null): string {
  if (!hectares) return '#94a3b8'; // gray for unknown
  if (hectares < 1) return BURNED_AREA_COLORS.small;
  if (hectares < 10) return BURNED_AREA_COLORS.medium;
  if (hectares < 100) return BURNED_AREA_COLORS.large;
  return BURNED_AREA_COLORS.severe;
}
